import type { DestinationMiddlewareParams } from "@segment/analytics-next/dist/types/plugins/middleware";
import { CategoryType } from "mws-ts-v2-sdk";

const CATEGORY_VIEWED_EVENT_NAME = "Category Viewed";
const EVENT_VIEWED_EVENT_NAME = "Event Viewed";
const PRODUCT_VIEWED_EVENT_NAME = "Product Viewed";
const BID_PLACED_EVENT_NAME = "Bid Placed";
const REGISTRATION_SUCCEEDED_EVENT_NAME = "Registration Succeeded";
const BID_CLICKED = "Bid Clicked";
const MAX_BID_CLICKED = "Max Bid Clicked";

/**
 * We want to use the team id for the content_id in the facebook pixel events.
 * This is because the products are short-lived and trying to feed product ids
 * for Facebook ad optimisation doesn't result in good ad performance.
 */
export const facebookDestinationMiddleware =
  () =>
  async ({ payload, next }: DestinationMiddlewareParams) => {
    if (payload.obj.type !== "track" || !payload.obj.properties) {
      return next(payload);
    }

    const setContentAsTeamId = (teamId: string) => {
      if (!payload.obj.properties) return;

      // for custom events, we need to manually map to content_id
      const contents = payload.obj.properties.contents;
      if (Array.isArray(contents)) {
        payload.obj.properties.contents = contents.map((content) => ({
          ...content,
          id: teamId,
        }));
      }

      payload.obj.properties.content_ids = [teamId];

      // To handle segments automatic mapping
      payload.obj.properties.product_id = teamId;
    };

    let teamId = payload.obj.properties?.categories?.team.id;

    switch (payload.obj.event) {
      case CATEGORY_VIEWED_EVENT_NAME: {
        const categoryType = payload.obj.properties?.category_type;
        if (
          categoryType &&
          categoryType === CategoryType.Team &&
          payload.obj.properties?.category_id
        ) {
          // we only send view content pages with a team category
          payload.obj.event = PRODUCT_VIEWED_EVENT_NAME;
          teamId = payload.obj.properties?.category_id;
        }

        break;
      }
      case EVENT_VIEWED_EVENT_NAME: {
        payload.obj.event = PRODUCT_VIEWED_EVENT_NAME;

        break;
      }
      case BID_PLACED_EVENT_NAME: {
        // Facebook uses the eventID param for de-duplication instead of event_id.
        // For some reason, we need to send this as messageId
        if (payload.obj.properties?.bid_id) {
          payload.obj.messageId = payload.obj.properties?.bid_id;
        }
        break;
      }
      case BID_CLICKED:
      case MAX_BID_CLICKED: {
        if (payload.obj.properties?.team_id) {
          teamId = payload.obj.properties?.team_id;
        }
        break;
      }
      case REGISTRATION_SUCCEEDED_EVENT_NAME: {
        if (payload.obj.userId) {
          payload.obj.messageId = payload.obj.userId;
        }
      }
    }

    if (teamId) {
      setContentAsTeamId(teamId);
    }

    return next(payload);
  };
